import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { KeyRound } from 'lucide-react';
import { ACCOUNT_VAULT_PASSWORD_MIN_LENGTH, type AccountVaultStatusDto } from '@gc/shared';

import { api } from '@/lib/api';
import { ghostPillClass, inputClass, pillClass } from './controls';
import { checkVaultPasswordForm, isOwnerOnlyRefusal } from './gameAccountForm';
import { toFieldErrors, useResource } from './useSettings';

/**
 * The accounts vault password (owner, 2026-09-08).
 *
 * What a PC asks for before its Accounts app shows any venue account's
 * password or mailbox password. One password for the whole venue, set by the
 * OWNER alone: it guards every launcher and mailbox the venue owns, so it is
 * long and mixed by contract (`setAccountVaultPasswordSchema`) and checked
 * here against that same schema before the PUT.
 *
 * Everyone who can open the screen sees whether it is set and since when —
 * that is what tells a shift the Accounts app on the PCs will open at all.
 * The form itself renders only for the owner; the API refuses anyone else.
 *
 * The typed password lives in this component's state and nowhere else: not
 * logged, not stored, cleared the moment the save lands.
 */
export function AccountVaultCard({ canSet }: { canSet: boolean }) {
  const { t, i18n } = useTranslation();
  const status = useResource<AccountVaultStatusDto>('/settings/game-accounts/vault');

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const check = checkVaultPasswordForm(password, confirm);
  // Complaints appear once there is something to complain about, not on an
  // empty form; the submit button stays disabled either way.
  const passwordProblem = password.length > 0 ? check.passwordProblem : null;
  const showMismatch = confirm.length > 0 && check.mismatch;

  const setOn = (iso: string | null): string => {
    if (!iso) return t('settings.gameAccounts.vault.set');
    const parsed = new Date(iso);
    if (Number.isNaN(parsed.getTime())) return t('settings.gameAccounts.vault.set');
    return t('settings.gameAccounts.vault.setOn', {
      date: new Intl.DateTimeFormat(i18n.language, {
        dateStyle: 'medium',
        timeStyle: 'short',
        numberingSystem: 'latn',
      }).format(parsed),
    });
  };

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!check.ok || check.payload === null) return;
    setError(null);
    setSaved(false);
    setSubmitting(true);
    try {
      const next = await api.put<AccountVaultStatusDto>(
        '/settings/game-accounts/vault',
        check.payload,
      );
      status.setData(next);
      setPassword('');
      setConfirm('');
      setSaved(true);
    } catch (caught) {
      setError(
        isOwnerOnlyRefusal(caught)
          ? t('settings.gameAccounts.ownerOnly')
          : (toFieldErrors(caught, t('settings.gameAccounts.vault.saveFailed'))[0]?.message ??
              null),
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section className="rounded-card border border-subtle bg-raised p-4">
      <h2 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-content-secondary">
        <KeyRound className="h-4 w-4" aria-hidden />
        {t('settings.gameAccounts.vault.title')}
      </h2>
      <p className="mt-1 text-sm text-content-muted">{t('settings.gameAccounts.vault.purpose')}</p>

      <p className="mt-3 text-sm text-content" data-vault-status>
        {status.data === null ? (
          status.loading ? (
            <span className="text-content-muted">{t('common.loading')}</span>
          ) : (
            <span className="text-danger">{t('settings.gameAccounts.vault.loadFailed')}</span>
          )
        ) : status.data.isSet ? (
          setOn(status.data.updatedAt)
        ) : (
          <span className="text-ending">{t('settings.gameAccounts.vault.notSet')}</span>
        )}
      </p>

      {canSet ? (
        <form onSubmit={(event) => void handleSubmit(event)} className="mt-4 max-w-md">
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block">
              <span className="mb-1 block text-sm text-content-secondary">
                {t('settings.gameAccounts.vault.newPassword')}
              </span>
              <input
                type="password"
                value={password}
                onChange={(event) => {
                  setPassword(event.target.value);
                  setSaved(false);
                }}
                required
                minLength={ACCOUNT_VAULT_PASSWORD_MIN_LENGTH}
                autoComplete="new-password"
                className={inputClass}
              />
              <span className="mt-1 block text-xs text-content-muted">
                {t('settings.gameAccounts.vault.rule', { min: ACCOUNT_VAULT_PASSWORD_MIN_LENGTH })}
              </span>
              {passwordProblem ? (
                <span className="mt-1 block text-xs text-danger">
                  {t(`settings.gameAccounts.vault.${passwordProblem}`, {
                    min: ACCOUNT_VAULT_PASSWORD_MIN_LENGTH,
                  })}
                </span>
              ) : null}
            </label>

            <label className="block">
              <span className="mb-1 block text-sm text-content-secondary">
                {t('settings.gameAccounts.vault.confirmPassword')}
              </span>
              <input
                type="password"
                value={confirm}
                onChange={(event) => {
                  setConfirm(event.target.value);
                  setSaved(false);
                }}
                required
                autoComplete="new-password"
                className={inputClass}
              />
              {showMismatch ? (
                <span className="mt-1 block text-xs text-danger">
                  {t('settings.gameAccounts.vault.mismatch')}
                </span>
              ) : null}
            </label>
          </div>

          {error ? (
            <p role="alert" className="mt-3 text-sm text-danger">
              {error}
            </p>
          ) : null}

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <button type="submit" disabled={submitting || !check.ok} className={pillClass}>
              {t('settings.gameAccounts.vault.save')}
            </button>
            {password || confirm ? (
              <button
                type="button"
                onClick={() => {
                  setPassword('');
                  setConfirm('');
                  setError(null);
                }}
                className={ghostPillClass}
              >
                {t('common.cancel')}
              </button>
            ) : null}
            {saved ? (
              <span className="text-sm text-success">{t('settings.gameAccounts.vault.saved')}</span>
            ) : null}
          </div>
        </form>
      ) : (
        <p className="mt-2 text-xs text-content-muted">
          {t('settings.gameAccounts.vault.ownerSets')}
        </p>
      )}
    </section>
  );
}
